import React from 'react'
import { useTranslation } from 'react-i18next'
import { FlowGrid, Paragraph, Row, Separator } from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'

import StoryCard from './StoryCard'
import useProcessedStories from '../../../../../hooks/useProcessedStories/useProcessedStories'
import { useStoryManager } from '../../../../../providers/StoryProvider'
import { handleFetchText } from '../../../../../services/MemoriesAPI/memoriesAPI'

export default function StoryGallery({ stories }) {
  const [t] = useTranslation()
  const { setPendingEdits, setOriginalGalleryStoryData, pendingEdits, isStoryValid, characterLimits } =
    useStoryManager()

  const { maxTitleCount, maxTextCount } = characterLimits
  const { processedStories, loading, error } = useProcessedStories(stories)

  const onSelectGalleryStory = async (existingStory) => {
    const { title, url, id } = existingStory

    // Optimistically update the selection state immediately for responsive UI
    setPendingEdits({ storyId: id, title: '', description: '', isValidStory: false, isGalleryStory: true })

    const text = await handleFetchText(url, true)
    const trimmedTitle = title.trim()
    const trimmedStory = text.trim()
    const isValidStory = isStoryValid(trimmedTitle, trimmedStory)

    setOriginalGalleryStoryData({
      title: trimmedTitle,
      description: trimmedStory,
      storyId: id,
    })
    setPendingEdits({ storyId: id, title: trimmedTitle, description: trimmedStory, isValidStory, isGalleryStory: true })
  }

  if (loading) {
    return (
      <>
        <Separator />
        <Row alignX="center" alignY="middle">
          <NoticeLoading size="lg" />
        </Row>
        <Separator />
      </>
    )
  }

  if (error) {
    return (
      <>
        <Separator />
        <Paragraph>{t('StoryTabs.error.generic', 'An error occurred while loading the gallery.')}</Paragraph>
        <Separator />
      </>
    )
  }

  return (
    <>
      <Separator />
      {processedStories.length > 0 ? (
        <FlowGrid columnCount={2}>
          {processedStories.map((story) => (
            <StoryCard
              key={story.id}
              story={story}
              isSelected={story.id === pendingEdits?.storyId}
              onSelect={onSelectGalleryStory}
              maxTitleCount={maxTitleCount}
              maxTextCount={maxTextCount}
            />
          ))}
        </FlowGrid>
      ) : (
        <Paragraph size="sm">{t('GalleryStories.noStories.description', 'No stories are in your gallery.')}</Paragraph>
      )}
      <Separator />
    </>
  )
}
